// third party
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { RouterProvider } from 'react-router-dom';

// zercode packages
import { ZercodeIntlProvider } from '@zercode/i18n';
import { brandingToBerryConfig, ZercodeThemeProvider } from '@zercode/theme';

// Berry seed (wrapped via seam — see src/berry/ZERCODE-CHANGES.md)
import { ConfigProvider } from 'berry/contexts/ConfigContext';
import NavigationScroll from 'berry/layout/NavigationScroll';

// Berry chrome (menu/breadcrumbs) resolves its labels via <FormattedMessage id> WITHOUT a
// defaultMessage, so it needs a real catalog. This flat map (TR pilot strings) was previously
// supplied by Berry's <Locales>; feed it to ZercodeIntlProvider so the menu isn't raw keys.
import berryCatalog from './berry/utils/locales/en.json';

// C-1b: per-locale page catalogs (extraction id → translation). Populated for the DoD pages
// (login, dashboard, courses, users); the long tail falls back to each component's
// defaultMessage until translated. `pnpm i18n:extract` regenerates src/locales/_source.json.
import enMessages from './locales/en.json';
import trMessages from './locales/tr.json';
import deMessages from './locales/de.json';
import arMessages from './locales/ar.json';

// project imports
import { useBrandingStore } from './lib/brandingStore';
import AdminTasksEnvironment from './pages/operations/tasks/AdminTasksEnvironment';
import router from './routes';
import { berrySeam } from './theme/berrySeam';

// server state via TanStack Query — not SWR/Redux [STD-FE]
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30 * 1000 }
  }
});

// ==============================|| APP ||============================== //

export default function App() {
  // live branding: seeded before first paint (main.tsx), refreshed by the
  // branding settings screen after a successful save — theme updates live.
  const branding = useBrandingStore((s) => s.branding);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Berry's useConfig() inputs fed from Control Plane branding [STD-FE] */}
      <ConfigProvider overrides={brandingToBerryConfig(branding)}>
        <ZercodeThemeProvider branding={branding} berry={berrySeam}>
          {/* C-1/C-1b: shared @zercode/i18n provider (locale store-driven) replaces Berry's Locales.
              Berry chrome's id-only labels (base) + per-locale page catalogs layered on top. */}
          <ZercodeIntlProvider
            appMessages={{
              en: { ...berryCatalog, ...enMessages },
              tr: { ...berryCatalog, ...trMessages },
              de: { ...berryCatalog, ...deMessages },
              ar: { ...berryCatalog, ...arMessages }
            }}
          >
            <NavigationScroll>
              <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                {/* TCH-TASKS: what the shared task drawer needs from this app (client, routes, user picker). */}
                <AdminTasksEnvironment>
                  <RouterProvider router={router} />
                </AdminTasksEnvironment>
              </SnackbarProvider>
            </NavigationScroll>
          </ZercodeIntlProvider>
        </ZercodeThemeProvider>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
